import { Badge, Banner, Button, Field, Input, InputArea, Switch } from '@cloudflare/kumo';
import { FormPage, FormSection, RadioCards } from '../../components/ui/FormPage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS, Glyph } from '../../components/ui/icons';

interface Source { id: string; type: 'file' | 'url' | 'text'; name: string; url: string | null; status: 'PENDING' | 'INDEXING' | 'READY' | 'ERROR'; error: string | null; bytes: number; chunks: number; createdAt: string }
interface Hit { trecho: string; fonte: string; url: string | null; score: number; detalhes?: { vetor?: number; palavra?: number; rerank?: number } }
interface SearchOpts { retrievalType: string; matchThreshold: number; limit: number; queryRewrite: boolean; rerank: boolean }
const DEFAULT_OPTS: SearchOpts = { retrievalType: 'hybrid', matchThreshold: 0, limit: 5, queryRewrite: false, rerank: true };
const pct = (n?: number) => (n == null ? '—' : `${Math.round(n * 100)}%`);

const STATUS: Record<Source['status'], { label: string; color: 'green' | 'orange' | 'red' | 'neutral' }> = {
  PENDING: { label: 'Na fila', color: 'neutral' },
  INDEXING: { label: 'Indexando', color: 'orange' },
  READY: { label: 'Pronto', color: 'green' },
  ERROR: { label: 'Erro', color: 'red' },
};
const GLYPH: Record<Source['type'], (typeof ICONS)[keyof typeof ICONS]> = { file: ICONS.File, url: ICONS.Link, text: ICONS.Note };
const kb = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** IA › Base de conhecimento: documentos, páginas e textos que os agentes consultam */
export function AiKnowledgePage() {
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<{ configured: boolean; sources: Source[] } | null>(null);
  const [locked, setLocked] = useState<string | null>(null);
  const [adding, setAdding] = useState<'url' | 'text' | null>(null);
  const [url, setUrl] = useState('');
  const [text, setText] = useState({ title: '', text: '' });
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<Source | null>(null);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [opts, setOpts] = useState<SearchOpts>(DEFAULT_OPTS);
  const [open, setOpen] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const load = useCallback(async () => {
    const r = await fetchApi('/api/ai/knowledge');
    const j = await r.json().catch(() => ({}));
    if (r.status === 403) { setLocked(j.message || 'Recurso não incluído no seu plano'); return; }
    if (r.ok) setData(j);
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);
  // Enquanto houver fonte indexando, atualiza sozinho
  useEffect(() => {
    if (!data?.sources.some((s) => s.status === 'PENDING' || s.status === 'INDEXING')) return;
    const t = setTimeout(load, 8000);
    return () => clearTimeout(t);
  }, [data, load]);

  const post = async (path: string, body: unknown, ok: string) => {
    setBusy(true);
    const r = await fetchApi(path, { method: 'POST', body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível adicionar'); return false; }
    toast.success(ok); load(); return true;
  };

  const uploadMany = async (files: FileList | File[]) => { for (const f of Array.from(files)) await upload(f); };
  const upload = async (file: File) => {
    if (file.size > 4 * 1024 * 1024) { toast.error(`${file.name}: acima de 4 MB, divida em partes menores`); return; }
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('auth_token');
    setBusy(true);
    const r = await fetch('/api/ai/knowledge/file', { method: 'POST', body: fd, headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível enviar'); return; }
    toast.success(`${file.name} enviado para indexação`); load();
  };

  const search = async () => {
    setBusy(true);
    const r = await fetchApi('/api/ai/knowledge/search', { method: 'POST', body: JSON.stringify({ query, ...opts, matchThreshold: opts.matchThreshold || undefined }) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível buscar'); return; }
    setHits(j.results); setOpen(null);
  };

  if (locked) return <div className="page-content" style={{ paddingTop: 28 }}><Banner variant="default" description={<>{locked}</>} /></div>;

  if (adding === 'url') {
    return (
      <FormPage
        backTo="/ia/base"
        backLabel="Base de conhecimento"
        onBack={() => setAdding(null)}
        title={'Adicionar página do site'}
        description="A página é lida e indexada para os agentes responderem com ela."
        submitLabel={'Adicionar'}
        submitting={busy}
        submitDisabled={!/^https?:\/\/\S+\.\S+/.test(url.trim())}
        onSubmit={async () => { if (await post('/api/ai/knowledge/url', { url }, 'Página enviada para indexação')) { setAdding(null); setUrl(''); } }}
        onCancel={() => setAdding(null)}
      >
        <FormSection title="Detalhes">


        <div className="space-y-4">
          <Field label="Endereço">
            <Input onChange={(e) => setUrl(e.target.value)} placeholder="https://sualoja.com.br/perguntas-frequentes" value={url} />
          </Field>
          <div className="text-xs !text-[var(--text-muted)]">A página é aberta como num navegador (funciona com sites em JavaScript) e o texto vai para a base. Adicione cada página importante.</div>
          </div>
        </FormSection>
      </FormPage>
    );
  }

  if (adding === 'text') {
    return (
      <FormPage
        backTo="/ia/base"
        backLabel="Base de conhecimento"
        onBack={() => setAdding(null)}
        title="Adicionar texto"
        description="Escreva o conteúdo direto aqui: horários, políticas, respostas prontas."
        submitLabel="Adicionar"
        submitting={busy}
        submitDisabled={!text.text.trim()}
        onSubmit={async () => { if (await post('/api/ai/knowledge/text', text, 'Texto enviado para indexação')) { setAdding(null); setText({ title: '', text: '' }); } }}
        onCancel={() => setAdding(null)}
      >
        <FormSection title="Conteúdo">
          <div className="space-y-4">
            <Field label="Título"><Input onChange={(e) => setText({ ...text, title: e.target.value })} placeholder="Perguntas frequentes" value={text.title} /></Field>
            <InputArea label="Conteúdo" onChange={(e) => setText({ ...text, text: e.target.value })} rows={12} value={text.text} placeholder={'Horário: segunda a sábado, 8h às 18h\nEntrega: até 3 dias úteis em Palmas\nTrocas: em até 7 dias com a nota'} />
          </div>
        </FormSection>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div style={{ maxWidth: 760 }}>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Base de conhecimento</h2>
            <div className="text-kumo-subtle">
              Documentos, páginas do seu site e textos que os agentes consultam antes de responder (ferramenta "Consultar a base de conhecimento").
              Eles respondem só com o que estiver aqui e citam a fonte.
            </div>
          </div>
          {canEdit && data?.configured && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="secondary" icon={ICONS.Upload} disabled={busy} onClick={() => fileRef.current?.click()}>Arquivo</Button>
              <input ref={fileRef} type="file" multiple hidden accept=".pdf,.docx,.xlsx,.csv,.txt,.md,.html,.json" onChange={(e) => { if (e.target.files?.length) uploadMany(e.target.files); e.target.value = ''; }} />
              <Button variant="secondary" icon={ICONS.Link} onClick={() => setAdding('url')}>Página do site</Button>
              <Button variant="primary" icon={ICONS.Plus} onClick={() => setAdding('text')}>Texto</Button>
            </div>
          )}
        </div>
      </div>

      <div className="page-content space-y-6"
        onDragOver={(e) => { if (canEdit && data?.configured) { e.preventDefault(); setDragging(true); } }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); if (canEdit && data?.configured && e.dataTransfer.files.length) uploadMany(e.dataTransfer.files); }}
        style={dragging ? { outline: '2px dashed var(--crm-primary, #2563eb)', outlineOffset: -8 } : undefined}>
        {dragging && <Banner variant="default" description={<>Solte os arquivos para enviar à base.</>} />}
        {data && !data.configured && <Banner variant="alert" description={<>A base de conhecimento usa o Cloudflare AI Search, que ainda não foi configurado na plataforma. Fale com o suporte.</>} />}
        {data?.configured && data.sources.length === 0 && <Banner variant="default" description={<>Nada na base ainda. Envie a tabela de preços, o catálogo em PDF, as perguntas frequentes ou páginas do seu site.</>} />}

        {data && data.sources.length > 0 && (
          <section className="brand-card">
            <table className="w-full text-sm">
              <thead><tr className="text-left" style={{ color: 'var(--text-muted)' }}><th className="py-2">Fonte</th><th>Tamanho</th><th>Status</th><th /></tr></thead>
              <tbody>
                {data.sources.map((s) => (
                  <tr key={s.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td className="py-2">
                      <div className="flex items-center gap-2"><Glyph icon={GLYPH[s.type]} size={16}/>
                        {s.url ? <a href={s.url} target="_blank" rel="noreferrer" className="underline">{s.name}</a> : <span>{s.name}</span>}</div>
                      {s.error && <div className="text-xs !text-[var(--crm-text-error)]">{s.error}</div>}
                    </td>
                    <td>{s.bytes ? kb(s.bytes) : '—'}{s.chunks ? ` · ${s.chunks} trechos` : ''}</td>
                    <td><Badge variant={STATUS[s.status].color}>{STATUS[s.status].label}</Badge></td>
                    <td className="text-right">{canEdit && <DangerButton variant="dangerOutline" onClick={() => setDeleting(s)}>Excluir</DangerButton>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {data?.configured && data.sources.some((s) => s.status === 'READY') && (
          <section className="brand-card space-y-3">
            <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Testar a busca</h3>
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (query.trim()) search(); }}>
              <div className="flex-1">
                <Input onChange={(e) => setQuery(e.target.value)} aria-label="Pergunta" placeholder="Ex.: qual o prazo de entrega?" value={query} />
              </div>
              <Button type="submit" variant="secondary" icon={ICONS.MagnifyingGlass} loading={busy}>Buscar</Button>
            </form>
            <details className="text-sm">
              <summary className="cursor-pointer text-kumo-subtle">Ajustes da busca</summary>
              <div className="space-y-4 pt-3">
                <RadioCards label="Tipo de busca" columns={3} value={opts.retrievalType} onChange={(v) => setOpts({ ...opts, retrievalType: v })} options={[
                  { value: 'hybrid', title: 'Híbrida', description: 'Significado + palavras exatas' },
                  { value: 'vector', title: 'Por significado', description: 'Vetores (embeddings)' },
                  { value: 'keyword', title: 'Palavras', description: 'Termos exatos (nomes, códigos)' },
                ]} />
                <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                  <Field label={`Relevância mínima: ${Math.round(opts.matchThreshold * 100)}%`}>
                    <input type="range" min={0} max={0.9} step={0.05} value={opts.matchThreshold} onChange={(e) => setOpts({ ...opts, matchThreshold: Number(e.target.value) })} className="w-full" aria-label="Relevância mínima" />
                  </Field>
                  <Field label={`Trechos: ${opts.limit}`}>
                    <input type="range" min={1} max={10} step={1} value={opts.limit} onChange={(e) => setOpts({ ...opts, limit: Number(e.target.value) })} className="w-full" aria-label="Quantidade de trechos" />
                  </Field>
                </div>
                <div className="flex gap-6 flex-wrap">
                  <label className="flex items-center gap-2"><Switch checked={opts.rerank} onCheckedChange={(v: boolean) => setOpts({ ...opts, rerank: v })} aria-label="Reordenar por relevância" /> Reordenar por relevância</label>
                  <label className="flex items-center gap-2"><Switch checked={opts.queryRewrite} onCheckedChange={(v: boolean) => setOpts({ ...opts, queryRewrite: v })} aria-label="Reescrever a pergunta" /> Reescrever a pergunta</label>
                </div>
                <Button variant="ghost" onClick={() => setOpts(DEFAULT_OPTS)}>Restaurar padrão</Button>
              </div>
            </details>
            {hits && hits.length === 0 && <div className="text-sm !text-[var(--text-muted)]">Nada encontrado com esses ajustes. O agente vai dizer que confirma com a equipe.</div>}
            {hits && hits.length > 0 && <div className="text-xs !text-[var(--text-muted)]">{hits.length} {hits.length === 1 ? 'trecho' : 'trechos'}</div>}
            {hits?.map((h, i) => (
              <div key={i} style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                <div className="flex items-center gap-2 flex-wrap text-xs !text-[var(--text-muted)]">
                  <span>{h.fonte}</span><Badge variant={h.score >= 0.7 ? 'green' : h.score >= 0.4 ? 'orange' : 'neutral'}>relevância {pct(h.score)}</Badge>
                  {h.detalhes && <span>significado {pct(h.detalhes.vetor)} · palavras {pct(h.detalhes.palavra)} · reordenação {pct(h.detalhes.rerank)}</span>}
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }} className={open === i ? '' : 'line-clamp-3'}>{h.trecho}</div>
                {h.trecho.length > 240 && <button type="button" className="text-xs underline" onClick={() => setOpen(open === i ? null : i)}>{open === i ? 'Mostrar menos' : 'Ver trecho completo'}</button>}
              </div>
            ))}
          </section>
        )}
      </div>



      <DangerConfirmModal open={!!deleting} title="Excluir fonte" confirmText="Excluir" objectName={deleting?.name}
        onCancel={() => setDeleting(null)} onConfirm={async () => {
          if (deleting) { const r = await fetchApi(`/api/ai/knowledge/${deleting.id}`, { method: 'DELETE' }); if (r.ok) { toast.success('Fonte excluída'); load(); } else toast.error('Não foi possível excluir'); }
          setDeleting(null);
        }}>
        Os agentes deixam de usar <strong>{deleting?.name}</strong> nas respostas.
      </DangerConfirmModal>
    </>
  );
}
